package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.LChartIcon
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LFileIcon
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.onInkPalette
import dev.lingvo.site.ds.prettyText
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transition
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.iconSize

/**
 * Four vendors, one project, four different things being counted.
 *
 * The band next door argues that a sentence written once should not be billed twelve times. This one
 * holds the comparison still long enough to be checked: the *same* forty-seven keys, five hundred
 * and sixty-four strings, twelve languages and two admins are stated once, at the top, on a bar that
 * belongs to no vendor — and then each card only has to answer one question, which is what its
 * invoice decides to measure. Fix the project and the pricing model is the sole variable.
 *
 * **Nothing here quotes a competitor's money.** We cannot know which tier a given reader is on, and
 * an invented figure is a lie a prospect can check. What the cards carry is the metering unit, which
 * each vendor publishes, and the shape of the curve that unit implies. Verified against each
 * vendor's own pricing page — the same 2026-08-11 sourcing that [MultiplicationSection] carries, and
 * the same constraint applies to any rewrite of the copy below: name what is metered, or say nothing.
 *
 * **The Lingvo card is on ink and the other three are on paper.** That is the argument in the
 * layout: one of these four is not like the others. Ink is spent twice on the whole page, so this
 * card borrows the device at card scale rather than taking a band of its own.
 */
@Composable
fun MeteredBand() {
    Band(ground = BandGround.PaperSunken, labelledBy = METERED_HEADING_ID) {
        Div(MeteredHeadStyle.toModifier().toAttrs()) {
            Div(MeteredHeadLeadStyle.toModifier().toAttrs()) {
                BandLabel(t("marketing.metered_band.the_part_their_pricing_page_puts", "The part their pricing page puts in a footnote"))
                BandHeading(t("marketing.metered_band.everyone_counts_something_we_count_people", "Everyone counts something. We count people."), id = METERED_HEADING_ID)
            }
            P(MeteredNoteStyle.toModifier().toAttrs()) {
                Text(
                    t("marketing.metered_band.same_forty_seven_keys_same_twelve", "Same forty-seven keys. Same twelve languages. The only thing that changes is " +
                        "what the invoice decides to measure."),
                )
            }
        }

        SharedAssumption()

        Ul(MeteredGridStyle.toModifier().toAttrs()) {
            VENDORS.forEach { vendor -> MeteredCard(vendor) }
        }
    }
}

/**
 * The controlled variables, stated once so no card has to restate them.
 *
 * Without this bar each card would need its own "for a project of 47 keys…" preamble, and four
 * copies of one premise is how a comparison starts looking like four separate claims. It is also the
 * honest place for it: the numbers are ours, they apply to every column equally, and putting them on
 * paper rather than inside a card keeps them from reading as any one vendor's marketing.
 */
@Composable
private fun SharedAssumption() {
    Div(SharedAssumptionStyle.toModifier().toAttrs()) {
        Span(SharedClaimStyle.toModifier().toAttrs()) {
            LFileIcon(Modifier.iconSize(IconSize.MD))
            Text(t("marketing.metered_band.the_same_project_priced_four_ways", "the same project, priced four ways"))
        }
        Ul(SharedFactsStyle.toModifier().toAttrs()) {
            SHARED_FACTS.forEach { (amount, unit) ->
                Li(SharedFactStyle.toModifier().toAttrs()) {
                    Span(SharedFactAmountStyle.toModifier().toAttrs()) { Text(amount) }
                    Span(SharedFactUnitStyle.toModifier().toAttrs()) { Text(unit) }
                }
            }
        }
    }
}

/**
 * One vendor, one metering unit.
 *
 * The card is an `li` rather than a `div` inside one, so the four of them are a list to a screen
 * reader as well as to the eye — this is a set of comparable things, and a reader who cannot see the
 * grid should still be told how many there are.
 */
@Composable
private fun MeteredCard(vendor: MeteredVendor) {
    val onInk = vendor.onInk
    Li(
        MeteredCardStyle.toModifier()
            .then(if (onInk) MeteredCardInkStyle.toModifier() else Modifier)
            .toAttrs(),
    ) {
        Div(MeteredTopStyle.toModifier().toAttrs()) {
            Div(
                MeteredTileStyle.toModifier()
                    .then(if (onInk) MeteredTileInkStyle.toModifier() else Modifier)
                    // The tile is a mark sitting next to a name that already says the same word, so
                    // it is decoration and announcing it would only double the vendor's name.
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                if (onInk) {
                    // Lingvo's mark: a bracket around a glyph. Deliberately *not* [LWordmarkGlyph],
                    // which rotates the glyph on a timer — the script wall is the only self-starting
                    // motion this page is allowed, and a second one inside a card would break that
                    // budget for a mark nobody is looking at.
                    Text("[")
                    // Load-bearing. Han and kana are unified in Unicode, so the face that draws あ is
                    // picked by this attribute through `:lang()` and by nothing else.
                    Span(Modifier.toAttrs { attr("lang", "ja") }) {
                        Text("あ") // i18n-ignore: a kana specimen; its `lang` picks the face
                    }
                    Text("]")
                } else {
                    Text(vendor.name.take(1))
                }
            }
            Span(MeteredNameStyle.toModifier().toAttrs()) { Text(vendor.name) }
        }

        Div(MeteredMeterGroupStyle.toModifier().toAttrs()) {
            Span(
                MeteredLabelStyle.toModifier()
                    .then(if (onInk) MeteredLabelInkStyle.toModifier() else Modifier)
                    .toAttrs(),
            ) { Text(t("marketing.metered_band.metered_by", "Metered by")) }
            Span(
                MeteredMeterStyle.toModifier()
                    .then(if (onInk) MeteredMeterInkStyle.toModifier() else Modifier)
                    .toAttrs(),
            ) { Text(vendor.meter) }
        }

        P(
            MeteredLineStyle.toModifier()
                .then(if (onInk) MeteredLineInkStyle.toModifier() else Modifier)
                .toAttrs(),
        ) { Text(vendor.line) }

        MeterChart(vendor)

        Div(
            MeteredTagStyle.toModifier()
                .then(if (onInk) MeteredTagInkStyle.toModifier() else Modifier)
                .toAttrs(),
        ) {
            if (vendor.growsPerLocale) {
                LChartIcon(Modifier.iconSize(IconSize.XS))
            } else {
                LCheckIcon(Modifier.iconSize(IconSize.XS))
            }
            Text(vendor.tag)
        }
    }
}

/**
 * Four bars: what one locale costs, and what twelve do.
 *
 * **The bars are hidden from assistive technology and the caption below them is not.** A screen
 * reader given four unlabelled rectangles learns nothing; given "meter · 1 → 12 locales" beside the
 * word `words` it has the entire claim. The picture is a second channel for the same sentence, not
 * an extra one.
 *
 * **Height carries the reading, not colour.** The last bar is tinted because it is the one you are
 * actually billed for at twelve locales, but it is also the tallest by a wide margin, so the shape
 * survives greyscale, a colour-blind reader, and a printed page. A tint that was the only difference
 * would be a claim only some readers could see.
 */
@Composable
private fun MeterChart(vendor: MeteredVendor) {
    val onInk = vendor.onInk
    Div(MeteredChartStyle.toModifier().toAttrs()) {
        Div(MeteredBarsStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
            vendor.bars.forEachIndexed { index, barHeight ->
                val billed = vendor.growsPerLocale && index == vendor.bars.lastIndex
                Div(
                    MeteredBarStyle.toModifier()
                        .then(if (billed) MeteredBarBilledStyle.toModifier() else Modifier)
                        .then(if (onInk) MeteredBarInkStyle.toModifier() else Modifier)
                        .height(barHeight.px)
                        .toAttrs(),
                )
            }
        }
        // The caption is looked up here rather than carried on [MeteredVendor]. It was `tr(...)`
        // inside that top-level `val`, which initialises at class-init — before `I18n.bootstrap()`
        // has installed a catalogue — so the lookup returned its own English for the life of the
        // page while the extractor dutifully minted a key a translator could fill in and never see
        // rendered. That is worse than leaving the string untranslated, and it is the trap the nav
        // model, the price card and the limits rows each carry a comment about. There are only two
        // captions and `growsPerLocale` already tells them apart, so no per-vendor field is needed.
        Span(
            MeteredCaptionStyle.toModifier()
                .then(if (onInk) MeteredCaptionInkStyle.toModifier() else Modifier)
                .toAttrs(),
        ) {
            Text(
                if (vendor.growsPerLocale) {
                    t("marketing.metered_band.meter_1_12_locales", "meter · 1 → 12 locales")
                } else {
                    t("marketing.metered_band.flat_1_12_locales", "flat · 1 → 12 locales")
                },
            )
        }
    }
}

private const val METERED_HEADING_ID = "metered-heading"

/**
 * The project every card is priced against. Ours, and small on purpose — a project this size is
 * where the difference between metering units is easiest to check by hand.
 */
private val SHARED_FACTS = listOf(
    "47" to "keys",
    "564" to "strings",
    "12" to "languages",
    "2" to "admins",
)

private data class MeteredVendor(
    val name: String,
    val meter: String,
    val line: String,
    /**
     * Bar heights in pixels, and genuinely geometry rather than type: this is a chart, and a chart's
     * columns are measured against the track they sit in ([BAR_TRACK_PX]) rather than against the
     * type scale. Snapping them to spacing steps would flatten the curve the chart exists to show.
     */
    val bars: List<Int>,
    val tag: String,
    /** Whether the meter multiplies with the locale count. Drives the tag icon and the tinted bar. */
    val growsPerLocale: Boolean,
    val onInk: Boolean = false,
)

/** The track the bars are measured in. One height for all four charts, or the curves are not comparable. */
private const val BAR_TRACK_PX = 34

/**
 * Ours first, then the three whose published unit multiplies with the locale count.
 *
 * Weblate is last and its tag is the escape hatch rather than the complaint: its hosted plans meter
 * strings like the other two, but the project is genuinely free to self-host, and omitting that to
 * keep the row tidy would be the kind of comparison this page is arguing against.
 */
private val VENDORS = listOf(
    MeteredVendor(
        name = "Lingvo",
        meter = "seats",
        line = "Two admins on the invoice. Translators and viewers cost nothing.",
        // Flat is the whole point, so the four bars are identical. Equality is the shape here.
        bars = listOf(12, 12, 12, 12),
        tag = "flat, forever",
        growsPerLocale = false,
        onInk = true,
    ),
    MeteredVendor(
        name = "Crowdin",
        meter = "words",
        line = "Add a language and the same sentence is metered a second time.",
        bars = listOf(7, 15, 25, BAR_TRACK_PX),
        tag = "grows per locale",
        growsPerLocale = true,
    ),
    MeteredVendor(
        name = "Transifex",
        meter = "words",
        line = "Words again — the count follows every locale you decide to add.",
        bars = listOf(7, 16, 26, BAR_TRACK_PX),
        tag = "grows per locale",
        growsPerLocale = true,
    ),
    MeteredVendor(
        name = "Weblate",
        meter = "strings",
        line = "Hosted plans meter strings. Genuinely free if you run it yourself.",
        bars = listOf(6, 13, 22, 30),
        tag = "or self-host, free",
        growsPerLocale = true,
    ),
)

/**
 * Resting and lifted shadows.
 *
 * The resting one is nearly invisible and exists only so the hover has something to grow *from* —
 * a card that goes from no shadow to a shadow reads as switching on rather than rising. On ink the
 * same alpha does nothing, so the dark values are steeper rather than proportional.
 */
private fun cardRestShadow(dark: Boolean): String =
    if (dark) "0 1px 2px -1px rgba(0, 0, 0, .55)" else "0 1px 2px -1px rgba(20, 18, 15, .10)"

private fun cardLiftShadow(dark: Boolean): String =
    if (dark) "0 14px 28px -14px rgba(0, 0, 0, .74)" else "0 14px 28px -14px rgba(20, 18, 15, .30)"

/**
 * Heading left, qualifying sentence right, both sitting on the same baseline.
 *
 * The note is a caveat on the heading rather than an introduction to the cards, so it belongs beside
 * the heading and bottom-aligned to it. Stacked above the cards instead, it would read as a third
 * thing the band is saying.
 */
val MeteredHeadStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.XL)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
            }
    }
    Breakpoint.LG {
        Modifier
            .gap(Space.Hero)
            .styleModifier {
                // 380px is a measure, not a spacing step: it is the column width that keeps the note
                // near sixty characters a line next to a 640px heading.
                property("grid-template-columns", "minmax(0, 1fr) 380px")
                property("align-items", "end")
            }
    }
}

val MeteredHeadLeadStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .gap(Space.LG)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
        }
}

val MeteredNoteStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textSecondary)
        .prettyText()
}

/**
 * The premise bar: paper on a sunken band, so it reads as laid on the page rather than cut into it.
 *
 * Wraps rather than scrolls. Four facts and a claim will not fit one line on a phone, and a
 * horizontally scrolling row of numbers hides half the premise behind a gesture nobody performs.
 */
val SharedAssumptionStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Card)
        .padding(topBottom = Space.LG, leftRight = Space.XL)
        .gap(Space.LG)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.SpaceBetween)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
        }
}

val SharedClaimStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .gap(Space.SM)
        .alignItems(AlignItems.Center)
        // The design's `p-ink-3` measures 2.66:1 on paper. textMuted is the same role at 5.21:1,
        // which is what this line needs to be, given it states the premise of the whole band.
        .color(colorMode.toSitePalette().textMuted)
        .styleModifier { property("display", "inline-flex") }
}

val SharedFactsStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .padding(0.px)
        .gap(Space.XL)
        .alignItems(AlignItems.Center)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("list-style", "none")
        }
}

val SharedFactStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .alignItems(AlignItems.Center)
        .styleModifier { property("display", "inline-flex") }
}

/**
 * The amounts. Tabular figures because `47`, `564`, `12` and `2` sit in one row and are meant to be
 * read as one set of measurements rather than as four unrelated words.
 */
val SharedFactAmountStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Body)
        .fontWeight(800)
        .letterSpacing((-0.5).px)
        .lineHeight(1)
        .color(colorMode.toSitePalette().textPrimary)
        .tabularNumbers()
}

val SharedFactUnitStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .color(colorMode.toSitePalette().textMuted)
}

/**
 * Four, then two, then one.
 *
 * Four cards of this density need roughly 240px each to keep the meter word on one line; below the
 * large breakpoint they pair off, and on a phone they stack. Two-up rather than straight to one-up,
 * because the comparison survives being read in pairs and does not survive being read as a list of
 * four unrelated cards a screen apart.
 */
val MeteredGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px)
            .padding(0.px)
            .gap(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("list-style", "none")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") }
    }
}

/**
 * The card, and the only thing on this band that moves.
 *
 * Hover lifts it [Motion.HoverLiftPx] and deepens the shadow over [Motion.StandardMs] — the two
 * halves of one gesture, so they share a duration. Transform and box-shadow are named individually
 * rather than transitioning `all`: `all` would animate the grid track width during a resize, which
 * is the exact stutter this page does not need while four cards reflow.
 *
 * There is no keyframe here and no entrance. The card is readable the instant it paints, in a
 * backgrounded tab and with JavaScript still loading; it moves only because a cursor arrived.
 */
val MeteredCardStyle = CssStyle {
    base {
        Modifier
            .minWidth(0.px)
            .padding(Space.XL)
            .gap(Space.LG)
            .backgroundColor(colorMode.toSitePalette().surfaceBase)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            // The design's `radius-lg` is 16, which on this scale is the step named Modal. The name
            // is about where 16 is normally spent, not a claim that this is a dialog.
            .borderRadius(Radius.Modal)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("box-shadow", cardRestShadow(colorMode.isDark))
            }
            .transition("transform", "box-shadow", durationMs = Motion.StandardMs, easing = Motion.EaseOut)
    }
    cssRule(CSSMediaQuery.Raw("(hover: hover) and (pointer: fine)"), ":hover") {
        Modifier
            .translateY((-Motion.HoverLiftPx).px)
            .styleModifier { property("box-shadow", cardLiftShadow(colorMode.isDark)) }
    }
}

/**
 * The one card that is not like the others.
 *
 * Border matched to the fill rather than removed, so the ink card keeps the same outer geometry as
 * the three beside it — a card that loses its 1px ring is a card that is 2px shorter than its
 * neighbours, and in a four-up row that misalignment is visible.
 */
val MeteredCardInkStyle = CssStyle.base {
    val ink = colorMode.onInkPalette()
    Modifier
        .backgroundColor(ink.surfaceBase)
        .color(ink.textPrimary)
        .border(1.px, LineStyle.Solid, ink.surfaceBase)
}

val MeteredTopStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.MD)
        .alignItems(AlignItems.Center)
        .styleModifier { property("display", "flex") }
}

/**
 * The mark tile. 26px square is geometry — it is sized to sit optically level with an 18px name
 * beside it, which no spacing step happens to land on.
 */
val MeteredTileStyle = CssStyle.base {
    Modifier
        .width(26.px)
        .height(26.px)
        .alignItems(AlignItems.Center)
        .justifyContent(JustifyContent.Center)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .color(colorMode.toSitePalette().textSecondary)
        .borderRadius(Radius.Control)
        .monoFont()
        .fontSize(TypeScale.Micro)
        .fontWeight(700)
        .lineHeight(1)
        .styleModifier {
            property("display", "flex")
            property("flex", "0 0 auto")
        }
}

val MeteredTileInkStyle = CssStyle.base {
    val ink = colorMode.onInkPalette()
    Modifier
        .backgroundColor(ink.accent)
        // Not paper. Paper on the night accent measures 2.95:1 and fails; `accentContrast` is the
        // token that exists precisely because the right answer flips with the mode, and it lands at
        // 5.72:1 on that same fill.
        .color(ink.accentContrast)
}

/** No colour of its own: the name is the card's own foreground, ink card or paper card. */
val MeteredNameStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Lead)
        .fontWeight(700)
        .letterSpacing((-0.5).px)
        .lineHeight(1.2)
}

/** Label and meter word are one unit, tighter than the card's rhythm, so they read as a pair. */
val MeteredMeterGroupStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
        }
}

val MeteredLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .letterSpacing(TypeScale.MicroTracking)
        .lineHeight(1)
        .color(colorMode.toSitePalette().textMuted)
        .styleModifier { property("text-transform", "uppercase") }
}

val MeteredLabelInkStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().textMuted)
}

/** The word the whole card exists to deliver. Nothing else on the card is above body size. */
val MeteredMeterStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.Heading)
        .fontWeight(800)
        .lineHeight(TypeScale.DisplayLineHeight)
        .letterSpacing((-1.3).px)
}

/** Ours is the accent because it is the answer, not another entry in the comparison. */
val MeteredMeterInkStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().accent)
}

/**
 * The sentence under the meter word.
 *
 * `min-height` is expressed in the line box rather than in pixels: three lines of this size is what
 * the longest of the four sentences needs, and holding that floor keeps the four charts on one
 * baseline across the row. It is a floor and not a height, so a shorter sentence at one-up simply
 * takes the space it needs.
 */
val MeteredLineStyle = CssStyle.base {
    Modifier
        .margin(0.px)
        .fontSize(TypeScale.Small)
        .lineHeight(TypeScale.BodyLineHeight)
        .color(colorMode.toSitePalette().textSecondary)
        .prettyText()
        .styleModifier { property("min-height", "${TypeScale.BodyLineHeight * 3}em") }
}

/**
 * Secondary rather than muted on ink, and deliberately not matching the design here: the design puts
 * the Lingvo card's sentence one step dimmer than the other three, which makes our own claim the
 * hardest of the four to read.
 */
val MeteredLineInkStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().textSecondary)
}

val MeteredChartStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            // Pushes the chart and the tag below it to the bottom of the card, so the four tags line
            // up whatever each sentence wrapped to.
            property("margin-top", "auto")
        }
}

/** The track. Fixed height in pixels because the bars are measured against it. */
val MeteredBarsStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .height(BAR_TRACK_PX.px)
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "flex-end")
        }
}

/**
 * A single column. 3px corners are part of the bar's geometry, not a surface radius — the shortest
 * bar is 6px tall, and any step on the radius scale would round it into a lozenge.
 */
val MeteredBarStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().border)
        .styleModifier {
            property("flex", "1 1 0")
            property("border-radius", "3px")
        }
}

/** The bar you are actually billed for at twelve locales. Also the tallest, so the tint is a second signal. */
val MeteredBarBilledStyle = CssStyle.base {
    Modifier.backgroundColor(colorMode.toSitePalette().textSecondary)
}

/** Flat means every bar is the billed one, so they all take the accent. */
val MeteredBarInkStyle = CssStyle.base {
    Modifier.backgroundColor(colorMode.onInkPalette().accent)
}

val MeteredCaptionStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .lineHeight(1)
        .color(colorMode.toSitePalette().textMuted)
}

val MeteredCaptionInkStyle = CssStyle.base {
    Modifier.color(colorMode.onInkPalette().accent)
}

/**
 * The verdict, as a pill.
 *
 * `align-self` keeps it hugging its own text rather than stretching to the card — a full-width pill
 * reads as a button, and nothing on this card is clickable. The icon is not decoration: it separates
 * "grows" from "flat" for a reader who is scanning the row rather than reading it, which is a
 * distinction the tag colours alone do not carry.
 */
val MeteredTagStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .padding(topBottom = Space.XS, leftRight = Space.MD)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .color(colorMode.toSitePalette().textSecondary)
        .borderRadius(Radius.Pill)
        .monoFont()
        .fontSize(TypeScale.Micro)
        .lineHeight(1)
        .alignItems(AlignItems.Center)
        .styleModifier {
            property("display", "inline-flex")
            property("align-self", "flex-start")
        }
}

val MeteredTagInkStyle = CssStyle.base {
    val ink = colorMode.onInkPalette()
    Modifier
        .backgroundColor(ink.accent)
        .color(ink.accentContrast)
}
